import {getSequenceStandardizedMedicines} from '@/modules/patients-therapies/functions'
import {useSequenceJourneyState} from '@/modules/patients-therapies/hooks'
import {DetailCard} from '@cypro/ui/components/ui/detail-card'
import {Badge, Box, BoxProps, HStack, Separator, useBreakpointValue} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {formatTreatmentLabels} from '../functions'
import {multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {DrugTag} from '@/shared/components/ui/tags/drug-tag'

interface Props extends BoxProps {
  sequence: PatientTherapySequence
  collapsible?: string
}

export function PatientTherapySequenceDetailCard({sequence, collapsible, ...props}: Props) {
  const {t} = useTranslate('common')
  const {t: tActivity} = useTranslate('activity')

  const isMobile = useBreakpointValue({base: true, md: false}) ?? false
  const journey = useSequenceJourneyState(sequence)

  const standardizedMedicines = getSequenceStandardizedMedicines(sequence)
  const {carrierDetails} = formatTreatmentLabels(sequence, t)

  return (
    <DetailCard.Root {...props} collapsible={collapsible}>
      <DetailCard.Outer>
        <HStack width="100%" alignItems="center" justifyContent="space-between">
          <HStack gapX="3">
            <DetailCard.OuterSection
              title={sequence.time}
              description={sequence.duration ? t('x_min', {value: sequence.duration}) : null}
              wordBreak="keep-all"
              textAlign="center"
            />

            {!sequence.isNote && (
              <>
                <Separator orientation="vertical" height="10" />
                <DetailCard.OuterSection
                  title={standardizedMedicines.map(item => (
                    <DrugTag key={item.activeSubstanceId} drug={item.activeSubstance} />
                  ))}
                  description={
                    sequence.patientProtocolId && (
                      <HStack gapX="2">
                        <span>{t('cycle_x', {value: sequence.cycle})}</span>
                        <Separator orientation="vertical" height="4" />
                        <span>{t('day_x', {value: sequence.day})}</span>
                      </HStack>
                    )
                  }
                />
              </>
            )}
          </HStack>

          {!isMobile && !sequence.isNote && sequence.status && (
            <HStack>
              <Badge
                size="sm"
                variant="surface"
                colorPalette={journey.getStepState(journey.currentPhaseKey).color?.split('.')?.[0]}
              >
                {tActivity(`${sequence.status}.title`)}
              </Badge>
            </HStack>
          )}
        </HStack>
      </DetailCard.Outer>

      <DetailCard.Inner display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} gapX="6" gapY="6">
        {!sequence.isNote && (
          <>
            <DetailCard.InnerSection
              title={t('medication')}
              description={standardizedMedicines.map(item => {
                const labels = []
                if (item.name) labels.push(item.name)
                labels.push(
                  [multipleDigitsNumberFormatter.format(item.totalAmount ?? 0), item.unit?.ucumCode]
                    .filter(Boolean)
                    .join(' ')
                )

                return <Box key={item.id}>{labels.join(' - ')}</Box>
              })}
            />

            <DetailCard.InnerSection title={t('carrier_solution')} description={carrierDetails.join(' ')} />

            <DetailCard.InnerSection title={t('administration_form')} description={sequence.applicationForm?.name} />

            <DetailCard.InnerSection
              title={t('administration_duration')}
              description={t('x_min', {value: sequence.duration})}
            />
          </>
        )}

        {sequence.note && (
          <DetailCard.InnerSection
            title={t('guideline_note')}
            description={sequence.note}
            gridColumnStart="1"
            gridColumnEnd="3"
          />
        )}
      </DetailCard.Inner>
    </DetailCard.Root>
  )
}
